/**
 * **Where the playhead may land while the viewer may not seek** — a publication's `seeking=false`
 * (`docs/plans/adc-rust.md` AD8).
 *
 * The player's own controls refuse the viewer's moves before they reach the media element: the
 * bar, the keyboard, the chapters, the transcript. This is the net beneath them, for the moves no
 * control of ours draws — the browser's own controls over the `<video>` (iOS's fullscreen player,
 * a picture-in-picture window's scrubber, Firefox's « Show controls »), which move the element
 * directly. The element says so with a `seeking` event, and this answers where to put the
 * playhead back.
 *
 * **What stands, whatever `seeking` says:**
 * - a move the player makes itself, announced with `permit` before it is made — the start offset
 *   a page asks for, the player's own resume point, the integrator's `seek` command, the guard's
 *   own putting back;
 * - a small step either way, which is the media pipeline's and not the viewer's: hls.js jumps a
 *   hole in the buffer, a browser rounds a position. **Steps that stood do not add up**: a step
 *   moves the playhead, not where playback had reached, which only playback moves, and every step
 *   is measured from there. A run of native seeks, each short of the bound, so carries the viewer
 *   no further than one would. Holes jumped that add up past the bound cost one put-back to where
 *   playback had reached; that move is the guard's own, and the next hole stands;
 * - a restart from the end — a `loop`, a replay once the media ended — which reaches nothing the
 *   viewer had not already played.
 *
 * Pure, so the rule is tested without an element.
 */

/** How far a move may land from the permitted position and still be the permitted one, in seconds. */
const LANDING_SECONDS = 0.5

/** The largest step the pipeline takes on its own: hls.js jumps a buffer hole of up to two seconds. */
const STEP_SECONDS = 2.5

/** How near the end the playhead must have been for a jump back to the start to be a restart. */
const END_SECONDS = 1.5

/** The span a playhead lives in: the media's, or a playlist entry's trim window. */
export interface SeekSpan {
  start: number
  end: number
}

export interface SeekGuard {
  /** The player is about to move the playhead to `time` itself: the seek landing there stands. */
  permit: (time: number) => void
  /**
   * Playback took the playhead to `time`. What it covered moves where a refused move puts the
   * playhead back; a step that stood on the way does not.
   */
  played: (time: number) => void
  /** A new media, whose playhead starts at `time`. */
  reset: (time?: number) => void
  /**
   * A seek landed at `time`: `null` when it stands, else the position to put the playhead back
   * to — after which the caller `permit`s that position and moves there.
   */
  landed: (time: number, span: SeekSpan) => number | null
}

/** A guard over one media element, refusing while `allowed` answers `false`. */
export function createSeekGuard(allowed: () => boolean): SeekGuard {
  // Where playback alone has taken the playhead: what a step is measured from, and where a refused
  // move puts the playhead back.
  let reached = 0
  // How far the small steps that stood carry the playhead from `reached`, either way. Each step is
  // measured from `reached`, so this never exceeds one step, however many were taken.
  let drift = 0
  let permitted: number | null = null

  /** The playhead is at `time` by a move that counts as playback's: nothing is carried any more. */
  function settle(time: number): null {
    reached = time
    drift = 0
    return null
  }

  return {
    permit(time) {
      permitted = time
    },
    played(time) {
      reached = time - drift
    },
    reset(time = 0) {
      settle(time)
      permitted = null
    },
    landed(time, span) {
      if (permitted !== null && Math.abs(time - permitted) <= LANDING_SECONDS) {
        permitted = null
        return settle(time)
      }
      if (allowed()) return settle(time)
      if (Math.abs(time - reached) <= STEP_SECONDS) {
        drift = time - reached
        return null
      }
      // Where the playhead stood before this move, the step it was carried by included.
      const stood = reached + drift
      const restarted = span.end > 0 && stood >= span.end - END_SECONDS && time <= span.start + LANDING_SECONDS
      if (restarted) return settle(time)
      return reached
    },
  }
}
